import React, { useEffect, useMemo, useState } from "react";
import { Plus, Upload, X, Trash2, Phone, MessageSquare, Mail, MessageCircle, StickyNote, CheckSquare, User, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/Page";

const STATUSES = [
  { id: "new", label: "New", color: "bg-gray-100 text-gray-800 border-gray-300" },
  { id: "contacted", label: "Contacted", color: "bg-blue-50 text-blue-800 border-blue-200" },
  { id: "qualified", label: "Qualified", color: "bg-amber-50 text-amber-800 border-amber-200" },
  { id: "proposal", label: "Proposal", color: "bg-purple-50 text-purple-800 border-purple-200" },
  { id: "won", label: "Won", color: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  { id: "lost", label: "Lost", color: "bg-red-50 text-red-800 border-red-200" },
];

const ACTIVITY_ICONS = {
  note: StickyNote, call: Phone, sms: MessageSquare,
  whatsapp: MessageCircle, email: Mail,
  status_change: CheckSquare, system: User,
};

const CRM = () => {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [leads, setLeads] = useState([]);
  const [kpis, setKpis] = useState({ total: 0, by_status: {} });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("kanban"); // kanban | list
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [drawerLead, setDrawerLead] = useState(null); // selected lead detail
  const [me, setMe] = useState(null);

  // Add-Lead form
  const [addForm, setAddForm] = useState({ name: "", email: "", phone: "", company: "", score: 0, notes: "" });
  const [adding, setAdding] = useState(false);

  // CSV import
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);

  // Load brands + me
  useEffect(() => {
    Promise.all([api.get("/brands"), api.get("/auth/me")]).then(([b, m]) => {
      const list = b.data.brands || [];
      setBrands(list);
      if (list[0]) setBrandId(list[0].id);
      setMe(m.data.user);
    }).catch(() => toast.error("Failed to load brands"));
  }, []);

  const loadLeads = async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      const params = { brand_id: brandId };
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/crm/leads", { params });
      setLeads(data.leads || []);
      setKpis(data.kpis || { total: 0, by_status: {} });
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLeads(); /* eslint-disable-line */ }, [brandId]);

  const onSearchSubmit = (e) => { e.preventDefault(); loadLeads(); };

  const grouped = useMemo(() => {
    const g = Object.fromEntries(STATUSES.map((s) => [s.id, []]));
    leads.forEach((l) => { if (g[l.status]) g[l.status].push(l); });
    return g;
  }, [leads]);

  const moveTo = async (lead, newStatus) => {
    if (lead.status === newStatus) return;
    // Optimistic update
    setLeads((prev) => prev.map((l) => l.id === lead.id ? { ...l, status: newStatus } : l));
    try {
      await api.patch(`/crm/leads/${lead.id}`, { status: newStatus });
      loadLeads();
      if (drawerLead?.id === lead.id) {
        const { data } = await api.get(`/crm/leads/${lead.id}`);
        setDrawerLead({ ...data.lead, activities: data.activities, tasks: data.tasks });
      }
    } catch (e) {
      toast.error("Move failed");
      loadLeads();
    }
  };

  const openLead = async (lead) => {
    try {
      const { data } = await api.get(`/crm/leads/${lead.id}`);
      setDrawerLead({ ...data.lead, activities: data.activities, tasks: data.tasks });
    } catch (e) {
      toast.error("Failed to open lead");
    }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim()) { toast.error("Name is required"); return; }
    setAdding(true);
    try {
      await api.post("/crm/leads", {
        brand_id: brandId,
        name: addForm.name.trim(),
        email: addForm.email || null,
        phone: addForm.phone || null,
        company: addForm.company || null,
        score: Number(addForm.score || 0),
        notes: addForm.notes || null,
        source: "manual", status: "new",
      });
      toast.success("Lead added");
      setShowAdd(false);
      setAddForm({ name: "", email: "", phone: "", company: "", score: 0, notes: "" });
      loadLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    } finally {
      setAdding(false);
    }
  };

  const submitImport = async (e) => {
    e.preventDefault();
    if (!csvFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("brand_id", brandId);
      fd.append("file", csvFile);
      const { data } = await api.post("/crm/leads/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} leads (${data.skipped} skipped)`);
      setShowImport(false);
      setCsvFile(null);
      loadLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  if (brands.length === 0) {
    return (
      <PageContainer>
        <PageHeader title="CRM" testId="crm-header" />
        <div className="op-card">
          <EmptyState
            icon={User}
            title="No brands available"
            description={me?.role === "crm_user"
              ? "You haven't been assigned to any brand yet. Ask your admin to assign you."
              : "Create a brand first, then start tracking leads in your pipeline."}
          />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="CRM"
        subtitle="Track every lead across the pipeline. Brand-scoped, fully searchable."
        testId="crm-header"
        actions={
          <div className="flex gap-2">
            <button onClick={() => setShowImport(true)} className="op-btn-secondary" data-testid="crm-import-btn"><Upload size={16} /> Import CSV</button>
            <button onClick={() => setShowAdd(true)} className="op-btn-primary" data-testid="crm-add-btn"><Plus size={16} /> Add Lead</button>
          </div>
        }
      />

      {/* Brand selector + filters */}
      <div className="op-card p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Brand:</label>
          <select className="op-input w-auto min-w-[200px]" value={brandId} onChange={(e) => setBrandId(e.target.value)} data-testid="crm-brand-select">
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <form onSubmit={onSearchSubmit} className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="op-input pl-9" placeholder="Search by name, email, phone…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="crm-search" />
          </div>
          <button type="submit" className="op-btn-secondary">Search</button>
        </form>
        <div className="ml-auto flex gap-1 bg-gray-100 rounded-lg p-1">
          <button onClick={() => setView("kanban")} className={`px-3 py-1 text-sm rounded ${view === "kanban" ? "bg-white shadow text-[#7C3AED]" : "text-gray-600"}`} data-testid="crm-view-kanban">Kanban</button>
          <button onClick={() => setView("list")} className={`px-3 py-1 text-sm rounded ${view === "list" ? "bg-white shadow text-[#7C3AED]" : "text-gray-600"}`} data-testid="crm-view-list">List</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
        {STATUSES.map((s) => (
          <div key={s.id} className={`op-card p-3 border-l-4 ${s.color}`} data-testid={`crm-kpi-${s.id}`}>
            <p className="text-xs text-gray-500 uppercase font-semibold">{s.label}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{kpis.by_status?.[s.id] ?? 0}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="op-card p-10 text-center text-gray-500">Loading leads…</div>
      ) : kpis.total === 0 ? (
        <div className="op-card">
          <EmptyState
            icon={User}
            title="No leads yet for this brand"
            description="Add your first lead manually, import from CSV, or share the public landing page URL from the Brands page."
            action={<button onClick={() => setShowAdd(true)} className="op-btn-primary"><Plus size={16} /> Add Lead</button>}
          />
        </div>
      ) : view === "kanban" ? (
        <KanbanBoard grouped={grouped} onMove={moveTo} onOpen={openLead} />
      ) : (
        <ListView leads={leads} onOpen={openLead} />
      )}

      {/* Add lead modal */}
      {showAdd && (
        <Modal onClose={() => setShowAdd(false)} title="Add Lead">
          <form onSubmit={submitAdd} className="space-y-3">
            <input className="op-input" placeholder="Full name *" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} data-testid="add-lead-name" />
            <input className="op-input" type="email" placeholder="Email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} />
            <input className="op-input" placeholder="Phone" value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} />
            <input className="op-input" placeholder="Company" value={addForm.company} onChange={(e) => setAddForm({ ...addForm, company: e.target.value })} />
            <div>
              <label className="text-xs text-gray-500">Lead score: {addForm.score}</label>
              <input type="range" min="0" max="100" className="w-full" value={addForm.score} onChange={(e) => setAddForm({ ...addForm, score: e.target.value })} />
            </div>
            <textarea className="op-input" rows={3} placeholder="Initial notes" value={addForm.notes} onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} />
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowAdd(false)} className="op-btn-secondary flex-1">Cancel</button>
              <button type="submit" disabled={adding} className="op-btn-primary flex-1" data-testid="add-lead-submit">{adding ? "Adding…" : "Add Lead"}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Import CSV modal */}
      {showImport && (
        <Modal onClose={() => setShowImport(false)} title="Import Leads from CSV">
          <form onSubmit={submitImport} className="space-y-3">
            <p className="text-sm text-gray-600">Recognized columns: <code className="bg-gray-100 px-1 rounded text-xs">name, email, phone, company</code>. The first row must be the header.</p>
            <input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} className="op-input" data-testid="csv-file-input" />
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowImport(false)} className="op-btn-secondary flex-1">Cancel</button>
              <button type="submit" disabled={importing || !csvFile} className="op-btn-primary flex-1" data-testid="csv-import-submit">{importing ? "Importing…" : "Import"}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Lead detail drawer */}
      {drawerLead && (
        <LeadDrawer
          lead={drawerLead}
          onClose={() => setDrawerLead(null)}
          onUpdate={async () => {
            const { data } = await api.get(`/crm/leads/${drawerLead.id}`);
            setDrawerLead({ ...data.lead, activities: data.activities, tasks: data.tasks });
            loadLeads();
          }}
          onDelete={async () => {
            if (!confirm("Permanently delete this lead and all its activities/tasks?")) return;
            await api.delete(`/crm/leads/${drawerLead.id}`);
            setDrawerLead(null);
            loadLeads();
            toast.success("Lead deleted");
          }}
        />
      )}
    </PageContainer>
  );
};

// ============ Kanban board ============
const KanbanBoard = ({ grouped, onMove, onOpen }) => {
  const [dragging, setDragging] = useState(null);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 overflow-x-auto">
      {STATUSES.map((s) => (
        <div key={s.id}
             className={`min-h-[300px] rounded-lg border-2 border-dashed ${dragging && dragging !== s.id ? 'border-purple-300 bg-purple-50/40' : 'border-gray-200 bg-gray-50/30'} p-2`}
             onDragOver={(e) => e.preventDefault()}
             onDrop={(e) => {
               e.preventDefault();
               const id = e.dataTransfer.getData("lead_id");
               const lead = Object.values(grouped).flat().find((l) => l.id === id);
               if (lead) onMove(lead, s.id);
               setDragging(null);
             }}
             data-testid={`kanban-col-${s.id}`}
        >
          <div className={`px-2 py-1 mb-2 text-xs font-bold uppercase tracking-wide rounded ${s.color} border inline-block`}>
            {s.label} <span className="ml-1 opacity-60">{grouped[s.id].length}</span>
          </div>
          <div className="space-y-2">
            {grouped[s.id].map((l) => (
              <div key={l.id}
                   draggable
                   onDragStart={(e) => { e.dataTransfer.setData("lead_id", l.id); setDragging(s.id); }}
                   onDragEnd={() => setDragging(null)}
                   onClick={() => onOpen(l)}
                   className="bg-white border border-gray-200 rounded-lg p-3 cursor-pointer hover:border-[#7C3AED] hover:shadow-sm transition"
                   data-testid={`lead-card-${l.id}`}
              >
                <p className="font-medium text-sm text-gray-900 truncate">{l.name}</p>
                {l.company && <p className="text-xs text-gray-500 truncate">{l.company}</p>}
                <div className="text-xs text-gray-500 mt-1 truncate">{l.email || l.phone || "—"}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">{l.source}</span>
                  {l.score > 0 && <span className="text-xs font-semibold text-[#7C3AED]">★ {l.score}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============ List view ============
const ListView = ({ leads, onOpen }) => (
  <div className="op-card overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr className="text-left text-xs font-semibold text-gray-600 uppercase">
          <th className="px-4 py-3">Name</th>
          <th className="px-4 py-3">Contact</th>
          <th className="px-4 py-3">Status</th>
          <th className="px-4 py-3">Source</th>
          <th className="px-4 py-3">Score</th>
          <th className="px-4 py-3">Created</th>
        </tr>
      </thead>
      <tbody>
        {leads.map((l) => {
          const s = STATUSES.find((x) => x.id === l.status);
          return (
            <tr key={l.id} onClick={() => onOpen(l)} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" data-testid={`list-row-${l.id}`}>
              <td className="px-4 py-3 font-medium text-gray-900">{l.name}</td>
              <td className="px-4 py-3 text-gray-600">{l.email || l.phone || "—"}</td>
              <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs rounded border ${s?.color}`}>{s?.label || l.status}</span></td>
              <td className="px-4 py-3 text-gray-600">{l.source}</td>
              <td className="px-4 py-3 font-semibold text-[#7C3AED]">{l.score || 0}</td>
              <td className="px-4 py-3 text-gray-500 text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// ============ Lead Drawer ============
const LeadDrawer = ({ lead, onClose, onUpdate, onDelete }) => {
  const [newNote, setNewNote] = useState("");
  const [noteType, setNoteType] = useState("note");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDue, setTaskDue] = useState("");

  const saveField = async (field, value) => {
    try {
      await api.patch(`/crm/leads/${lead.id}`, { [field]: value });
      onUpdate();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const addActivity = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/crm/leads/${lead.id}/activities`, { type: noteType, content: newNote.trim() });
      setNewNote("");
      onUpdate();
      toast.success("Activity logged");
    } catch (e) {
      toast.error("Failed to log");
    }
  };

  const addTask = async () => {
    if (!taskTitle.trim()) return;
    try {
      await api.post(`/crm/leads/${lead.id}/tasks`, {
        title: taskTitle.trim(),
        due_date: taskDue || null,
      });
      setTaskTitle(""); setTaskDue("");
      onUpdate();
      toast.success("Task added");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const toggleTask = async (t) => {
    try {
      await api.patch(`/crm/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" });
      onUpdate();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const removeTask = async (t) => {
    try {
      await api.delete(`/crm/tasks/${t.id}`);
      onUpdate();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="lead-drawer">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-start justify-between z-10">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-gray-900 truncate">{lead.name}</h2>
            <p className="text-sm text-gray-500 truncate">{lead.email || lead.phone || "—"}{lead.company && ` · ${lead.company}`}</p>
          </div>
          <div className="flex gap-1">
            <button onClick={onDelete} className="p-2 text-gray-500 hover:text-red-600 hover:bg-gray-50 rounded" title="Delete lead" data-testid="lead-drawer-delete">
              <Trash2 size={16} />
            </button>
            <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-50 rounded"><X size={16} /></button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Status + Score */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Status</label>
              <select value={lead.status} onChange={(e) => saveField("status", e.target.value)} className="op-input" data-testid="drawer-status">
                {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Score: {lead.score || 0}</label>
              <input type="range" min="0" max="100" value={lead.score || 0}
                     onChange={(e) => saveField("score", Number(e.target.value))}
                     className="w-full" data-testid="drawer-score" />
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-2">
            <Field label="Email" value={lead.email} onSave={(v) => saveField("email", v)} />
            <Field label="Phone" value={lead.phone} onSave={(v) => saveField("phone", v)} />
            <Field label="Company" value={lead.company} onSave={(v) => saveField("company", v)} />
            <Field label="Next follow-up (YYYY-MM-DD)" value={lead.next_followup} onSave={(v) => saveField("next_followup", v)} />
          </div>

          {/* Notes summary */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Notes</label>
            <textarea
              defaultValue={lead.notes || ""}
              rows={3}
              className="op-input"
              onBlur={(e) => { if (e.target.value !== (lead.notes || "")) saveField("notes", e.target.value); }}
              data-testid="drawer-notes"
            />
          </div>

          {/* Tasks */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2">Tasks</h3>
            <div className="space-y-1 mb-2">
              {(lead.tasks || []).map((t) => (
                <div key={t.id} className="flex items-center gap-2 bg-gray-50 rounded p-2" data-testid={`task-${t.id}`}>
                  <input type="checkbox" checked={t.status === "done"} onChange={() => toggleTask(t)} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${t.status === "done" ? "line-through text-gray-400" : "text-gray-900"}`}>{t.title}</p>
                    {t.due_date && <p className="text-xs text-gray-500">Due: {new Date(t.due_date).toLocaleDateString()}</p>}
                  </div>
                  <button onClick={() => removeTask(t)} className="text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input className="op-input flex-1" placeholder="New task…" value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} data-testid="task-title-input" />
              <input className="op-input w-40" type="date" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} />
              <button onClick={addTask} className="op-btn-primary" data-testid="task-add-btn"><Plus size={14} /></button>
            </div>
          </div>

          {/* Activities timeline */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2">Activity Timeline</h3>
            <div className="flex gap-2 mb-3">
              <select value={noteType} onChange={(e) => setNoteType(e.target.value)} className="op-input w-32">
                <option value="note">📝 Note</option>
                <option value="call">📞 Call</option>
                <option value="sms">💬 SMS</option>
                <option value="whatsapp">🟢 WhatsApp</option>
                <option value="email">📧 Email</option>
              </select>
              <input className="op-input flex-1" placeholder="Log activity…" value={newNote} onChange={(e) => setNewNote(e.target.value)} data-testid="activity-input"
                     onKeyDown={(e) => { if (e.key === "Enter") addActivity(); }} />
              <button onClick={addActivity} className="op-btn-primary" data-testid="activity-add-btn"><Plus size={14} /></button>
            </div>
            <div className="space-y-2">
              {(lead.activities || []).map((a) => {
                const Icon = ACTIVITY_ICONS[a.type] || StickyNote;
                return (
                  <div key={a.id} className="flex gap-3 text-sm" data-testid={`activity-${a.id}`}>
                    <div className="w-7 h-7 rounded-full bg-purple-50 text-[#7C3AED] flex items-center justify-center flex-shrink-0">
                      <Icon size={13} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900">{a.content}</p>
                      <p className="text-xs text-gray-400">{a.type} · {new Date(a.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onSave }) => {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{label}</label>
      <input className="op-input" value={v} onChange={(e) => setV(e.target.value)} onBlur={() => { if (v !== (value || "")) onSave(v); }} />
    </div>
  );
};

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div className="bg-white rounded-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X size={18} /></button>
      </div>
      {children}
    </div>
  </div>
);

export default CRM;
